<template>
  <div class="max-w-4xl mx-auto space-y-8">

    <!-- HEADER -->
    <div>
      <h1 class="text-3xl font-bold mb-2">Select Track for {{ childName }}</h1>
      <p class="text-[var(--mt-text-muted)]">
        Choose the appropriate 11+ curriculum track for this student.
      </p>
    </div>

    <!-- TRACK OPTIONS -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-6">

      <!-- CORE TRACK -->
      <Card
        class="p-5 cursor-pointer hover:shadow-xl transition border-2"
        :class="selectedClass('core')"
        @click="chooseTrack('core')"
      >
        <h2 class="text-xl font-bold text-brand-700">11+ Core</h2>
        <p class="text-gray-600 text-sm mt-1">
          Foundational skills for Year 4–5 students.
        </p>

        <ul class="text-sm mt-3 space-y-1 text-gray-700">
          <li>• Place Value & Number</li>
          <li>• Arithmetic Fluency</li>
          <li>• Fractions, Decimals & Percentages (intro)</li>
          <li>• Factors & Multiples</li>
        </ul>

        <button
          class="mt-4 w-full bg-brand-600 text-white py-2 rounded-lg hover:bg-brand-700"
        >
          Choose Core
        </button>
      </Card>

      <!-- INTENSIVE TRACK -->
      <Card
        class="p-5 cursor-pointer hover:shadow-xl transition border-2"
        :class="selectedClass('intensive')"
        @click="chooseTrack('intensive')"
      >
        <h2 class="text-xl font-bold text-amber-700">11+ Intensive</h2>
        <p class="text-gray-600 text-sm mt-1">
          Complete 11+ coverage with advanced reasoning.
        </p>

        <ul class="text-sm mt-3 space-y-1 text-gray-700">
          <li>• All Core lessons</li>
          <li>• BIDMAS & Powers</li>
          <li>• Advanced FDP</li>
          <li>• Ratio & Proportion</li>
        </ul>

        <button
          class="mt-4 w-full bg-amber-600 text-white py-2 rounded-lg hover:bg-amber-700"
        >
          Choose Intensive
        </button>
      </Card>

      <!-- MASTERY TRACK -->
      <Card
        class="p-5 cursor-pointer hover:shadow-xl transition border-2"
        :class="selectedClass('mastery')"
        @click="chooseTrack('mastery')"
      >
        <h2 class="text-xl font-bold text-green-700">11+ Mastery</h2>
        <p class="text-gray-600 text-sm mt-1">
          Designed for high achievers aiming for top 10% scores.
        </p>

        <ul class="text-sm mt-3 space-y-1 text-gray-700">
          <li>• All Intensive lessons</li>
          <li>• Challenging Powers & Surds</li>
          <li>• Mixed 11+ Challenge Sets</li>
          <li>• High-difficulty reasoning tasks</li>
        </ul>

        <button
          class="mt-4 w-full bg-green-600 text-white py-2 rounded-lg hover:bg-green-700"
        >
          Choose Mastery
        </button>
      </Card>

    </div>

    <!-- CONFIRMATION MODAL -->
    <transition name="fade">
      <div
        v-if="confirmOpen"
        class="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50"
      >
        <div class="bg-[var(--mt-card-bg)] rounded-xl p-6 shadow-xl w-full max-w-md">

          <h2 class="text-xl font-bold mb-3">Confirm Track</h2>
          <p class="text-sm text-gray-700 mb-6">
            Are you sure you want to set <strong>{{ childName }}</strong>
            to the <strong>{{ trackLabel(pendingTrack) }}</strong> curriculum?
          </p>

          <div class="flex justify-end gap-3">
            <button
              class="px-4 py-2 rounded-md bg-[var(--mt-badge-muted-bg)]"
              @click="confirmOpen = false"
            >
              Cancel
            </button>

            <button
              class="px-4 py-2 rounded-md bg-brand-600 text-white"
              @click="confirmTrack"
            >
              Yes, Select Track
            </button>
          </div>

        </div>
      </div>
    </transition>

  </div>
</template>

<script setup>
import { ref, computed } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import Card from '@/components/common/Card.vue';
import { useStudentStore } from '@/stores/studentStore';

const route = useRoute();
const router = useRouter();
const studentStore = useStudentStore();

const studentId = route.params.studentId;

// find child object
const child = computed(() =>
  studentStore.students.find(s => s.id === studentId)
);

const childName = computed(() => child.value?.name || "Student");

// track selection UX state
const confirmOpen = ref(false);
const pendingTrack = ref(null);

// CSS class for selected track box
function selectedClass(track) {
  return child.value?.track === track
    ? 'border-brand-600 shadow-lg'
    : 'border-transparent';
}

// Open confirmation modal
function chooseTrack(track) {
  pendingTrack.value = track;
  confirmOpen.value = true;
}

// Final confirm
function confirmTrack() {
  if (!child.value) return;

  child.value.track = pendingTrack.value;

  // Redirect to student's curriculum
  router.push(`/student/curriculum/${child.value.id}`);
}

// Convert track code to readable name
function trackLabel(track) {
  if (track === 'core') return '11+ Core';
  if (track === 'intensive') return '11+ Intensive';
  if (track === 'mastery') return '11+ Mastery';
  return 'Unknown';
}
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
